import { GraduationCap, BookOpen, FlaskConical, Clock } from "lucide-react";

const partA = [
  "Modules A1–A9 aligned to the SAA-C03 exam domains",
  "IAM & account security, EC2, EBS/EFS/S3 storage",
  "VPC networking & hybrid connectivity",
  "ELB, Auto Scaling, Route 53, CloudFront — resilient design",
  "Serverless (Lambda, API Gateway, SQS/SNS/EventBridge)",
  "KMS, WAF, GuardDuty; CloudWatch & CloudTrail",
  "Migration, Well-Architected Framework, cost optimization",
  "21.5 Hrs theory + 18.5 Hrs labs",
];

const partB = [
  "Modules B1–B12 built from the instructor's specialist decks",
  "RDS deep dive — HA, DR, backup, security, RDS Proxy",
  "Aurora — clusters, Global Database, Serverless v2",
  "Blue/Green deployments & major version upgrades",
  "Aurora MySQL & PostgreSQL performance tuning",
  "DMS/SCT migration; ElastiCache; DynamoDB end to end",
  "Redshift, EMR, Kinesis, Athena/Glue, OpenSearch, QuickSight",
  "31.5 Hrs theory + 28.5 Hrs labs + capstone project",
];

const tools = [
  "Console Navigation", "AWS CLI", "Terraform", "CloudFormation",
  "Real Architecture Diagrams", "Step-by-Step Labs",
];

const topics = [
  "EC2", "VPC", "S3", "Serverless", "RDS", "Aurora",
  "Blue/Green Upgrades", "Performance Tuning", "DynamoDB",
  "Migration (DMS/SCT)", "Analytics",
];

export default function CombinedAWSSection() {
  return (
    <section id="combined-course" data-testid="combined-course-section" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <GraduationCap size={24} className="text-[#6366F1]" />
        <p className="font-mono text-xs text-[#6366F1] uppercase tracking-widest">// combined program &middot; ongoing</p>
      </div>
      <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#F4F4F5] mb-2">
        AWS Solutions Architect <span className="text-[#6366F1]">+</span> AWS Database Architect
      </h2>
      <p className="font-['Space_Grotesk'] text-lg sm:text-xl text-[#A1A1AA] mb-2">
        Become a Complete AWS Cloud & Database Architect
      </p>
      <p className="text-sm text-[#71717A] italic mb-8 max-w-2xl">
        Every expert was once a beginner. Show up this Saturday — your cloud career starts with one class.
      </p>

      {/* First Class banner */}
      <div className="mb-8 p-4 bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-md inline-flex items-center gap-3 flex-wrap">
        <span className="font-['Space_Grotesk'] font-bold text-[#F4F4F5] text-sm uppercase tracking-wider">First Class</span>
        <span className="text-sm text-[#A1A1AA]">Saturday, 11 July</span>
        <span className="text-sm text-[#A1A1AA]">08:30 – 10:30 AM IST</span>
        <span className="text-sm text-[#6366F1] font-semibold">Live Online</span>
        <span className="px-2 py-0.5 bg-[#22C55E]/15 border border-[#22C55E]/30 rounded text-[10px] font-mono text-[#22C55E] uppercase tracking-wider">Ongoing</span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { value: "100", label: "Total Hours", icon: Clock },
          { value: "21", label: "Modules", icon: BookOpen },
          { value: "47", label: "Hands-On Lab Hrs", icon: FlaskConical },
          { value: "34", label: "Sessions x ~3 Hrs", icon: GraduationCap },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="p-4 bg-[#16161F] border border-[#27272A] rounded-md text-center">
              <Icon size={16} className="text-[#6366F1] mx-auto mb-2" />
              <p className="font-['Space_Grotesk'] text-2xl font-bold text-[#6366F1]">{stat.value}</p>
              <p className="text-xs text-[#71717A]">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Two parts */}
      <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[#F4F4F5] mb-1">Two Parts, One Architect Journey</h3>
      <p className="text-sm text-[#71717A] mb-6">Program Structure — 21 Modules + Capstone Project</p>

      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {/* Part A */}
        <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md hover:border-[#6366F1]/30 transition-all duration-300">
          <h4 className="font-['Space_Grotesk'] text-base font-bold text-[#F4F4F5] mb-1">
            PART A — AWS Solutions Architect Associate
          </h4>
          <p className="text-xs text-[#6366F1] font-mono mb-4">40 Hours</p>
          <ul className="space-y-2">
            {partA.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-[#A1A1AA]">
                <span className="text-[#6366F1] flex-shrink-0 mt-0.5">&#8226;</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Part B */}
        <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md hover:border-[#6366F1]/30 transition-all duration-300">
          <h4 className="font-['Space_Grotesk'] text-base font-bold text-[#F4F4F5] mb-1">
            PART B — AWS Database Architect
          </h4>
          <p className="text-xs text-[#6366F1] font-mono mb-4">60 Hours</p>
          <ul className="space-y-2">
            {partB.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-[#A1A1AA]">
                <span className="text-[#6366F1] flex-shrink-0 mt-0.5">&#8226;</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tools & Topics */}
      <div className="p-6 bg-[#111118] border border-[#27272A] rounded-md">
        <h4 className="font-['Space_Grotesk'] text-sm font-semibold text-[#6366F1] uppercase tracking-wider mb-3">Tools & Hands-On</h4>
        <div className="flex flex-wrap gap-2 mb-4">
          {tools.map((t) => (
            <span key={t} className="px-3 py-1.5 text-xs font-mono text-[#6366F1] bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-md">{t}</span>
          ))}
        </div>
        <h4 className="font-['Space_Grotesk'] text-sm font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3 mt-4">Topics Covered</h4>
        <div className="flex flex-wrap gap-2">
          {topics.map((t) => (
            <span key={t} className="px-3 py-1.5 text-xs font-mono text-[#A1A1AA] bg-[#0A0A0F] border border-[#27272A] rounded-md">{t}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
